import React, { useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import { Typography } from "@mui/material";

import messages from "../../../assets/locale/messages";
import { getCompanyDetailsRequest } from "../../../store/RegistrationReq/actions";
import { UIButton } from "../../ui";
import CompanyHeader from "./Header/CompanyHeader";
import CompanyInfoCard from "./Info/CompanyInfoCard";
import ContactPersonCard from "./Info/ContactPersonCard";
import DocumentsSection from "./Documents/DocumentsSection";
import DocumentViewer from "./Documents/DocumentViewer";

import "./CompanyViewDetails.scss";
import { buildOverviewDocuments } from "./Documents/buildOverviewDocuments";

const CompanyViewDetails = ({ onApprove, onRejectClick }) => {
  const { id } = useParams();
  const dispatch = useDispatch();
  const lang = useSelector((state) => state.locale.lang);
  const { registrationReq } = messages[lang];

  const companyDetails = useSelector(
    (state) => state.companiesRegistration.companyDetails
  );
  const companyDetailsLoading = useSelector(
    (state) => state.companiesRegistration.companyDetailsLoading
  );
  const companyDetailsError = useSelector(
    (state) => state.companiesRegistration.companyDetailsError
  );

  const [previewDoc, setPreviewDoc] = useState(null);

  useEffect(() => {
    if (!id) return;
    dispatch(getCompanyDetailsRequest({ id, force: true }));
  }, [id, lang, dispatch]);

  useEffect(() => {
    setPreviewDoc(null);
  }, [id]);

  const documents = useMemo(
    () => buildOverviewDocuments(companyDetails, registrationReq),
    [companyDetails, registrationReq]
  );


  if (companyDetailsError) {
    return (
      <div className="company-view-details py-4 text-center">
        <Typography color="error" className="mb-3">
          {companyDetailsError || registrationReq.companyDetailsLoadError}
        </Typography>
        <UIButton
          type="button"
          appearance="gradient"
          onClick={() =>
            dispatch(getCompanyDetailsRequest({ id, force: true }))
          }
        >
          {registrationReq.retry}
        </UIButton>
      </div>
    );
  }

  /* Global axios Loader (white spinner) handles fetch; avoid duplicate MUI primary (blue) spinner */
  if (companyDetailsLoading && !companyDetails) {
    return null;
  }

  if (!companyDetails) {
    return null;
  }

  return (
    <div className="company-view-details company-view-details--registration-request">
      <CompanyHeader
        company={companyDetails}
        onApprove={() => onApprove(companyDetails)}
        onRejectClick={() => onRejectClick(companyDetails)}
        hasEditProfileButton={false}
      />

      <div
        className={`company-view-layout${
          previewDoc ? " company-view-layout--balanced" : ""
        }`}
      >
        <div className="company-view-main">
          <CompanyInfoCard
            company={companyDetails}
          />
          <ContactPersonCard
            owner={companyDetails.company_owner}
          />
          <DocumentsSection
            documents={documents}
            onSelect={setPreviewDoc}
          />
        </div>
        <aside className="company-view-aside">
          <DocumentViewer
            document={previewDoc}
            messagesBundle={registrationReq}
            lang={lang}
            onClose={() => setPreviewDoc(null)}
          />
        </aside>
      </div>
    </div>
  );
};

CompanyViewDetails.propTypes = {
  onApprove: PropTypes.func.isRequired,
  onRejectClick: PropTypes.func.isRequired,
};

export default CompanyViewDetails;
